import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  formatGynecologicalHeightDisplay,
  formatGynecologicalWeightDisplay,
} from "@/encounters/components/GynecologicalEncounter/utils/gynecological-vitals-display";
import { GynecologicalEncounterVitalsSignQuestionsLinkIds } from "@/encounters/constants/gynecological-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import {
  formatTemperatureDisplay,
  isVitalValuePresentForDisplay,
} from "@/encounters/utils/assessments-vitals";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";
import { getQuestionnaireResponseAnswersSimplified } from "@/utils/medplum";

const linkIds = GynecologicalEncounterVitalsSignQuestionsLinkIds;

type VitalsDiffField = {
  label: string;
  previous: string;
  current: string;
};

const VitalsSignDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.GynecologicalEncounterVitalsSign,
          encounterId!,
        );
        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousAnswers = getQuestionnaireAnswers(previousVersion);
  const currentAnswers = getQuestionnaireAnswers(currentVersion);
  const previousDisplayAnswers = getQuestionnaireResponseAnswersSimplified(previousVersion);
  const currentDisplayAnswers = getQuestionnaireResponseAnswersSimplified(currentVersion);

  const fields: VitalsDiffField[] = [
    {
      label: "Blood Pressure",
      previous:
        previousAnswers[linkIds.GynecologicalSystolicBP]?.valueDecimal != null &&
        previousAnswers[linkIds.GynecologicalDiastolicBP]?.valueDecimal != null
          ? `${previousAnswers[linkIds.GynecologicalSystolicBP]?.valueDecimal}/${previousAnswers[linkIds.GynecologicalDiastolicBP]?.valueDecimal} mmHg`
          : "",
      current:
        currentAnswers[linkIds.GynecologicalSystolicBP]?.valueDecimal != null &&
        currentAnswers[linkIds.GynecologicalDiastolicBP]?.valueDecimal != null
          ? `${currentAnswers[linkIds.GynecologicalSystolicBP]?.valueDecimal}/${currentAnswers[linkIds.GynecologicalDiastolicBP]?.valueDecimal} mmHg`
          : "",
    },
    {
      label: "Heart Rate",
      previous: isVitalValuePresentForDisplay(
        previousAnswers[linkIds.GynecologicalHeartRate]?.valueInteger,
      )
        ? `${previousAnswers[linkIds.GynecologicalHeartRate]?.valueInteger} bpm`
        : "",
      current: isVitalValuePresentForDisplay(
        currentAnswers[linkIds.GynecologicalHeartRate]?.valueInteger,
      )
        ? `${currentAnswers[linkIds.GynecologicalHeartRate]?.valueInteger} bpm`
        : "",
    },
    {
      label: "Weight",
      previous: formatGynecologicalWeightDisplay(previousDisplayAnswers) ?? "",
      current: formatGynecologicalWeightDisplay(currentDisplayAnswers) ?? "",
    },
    {
      label: "Height",
      previous: formatGynecologicalHeightDisplay(previousDisplayAnswers) ?? "",
      current: formatGynecologicalHeightDisplay(currentDisplayAnswers) ?? "",
    },
    {
      label: "Temperature",
      previous: formatTemperatureDisplay(
        previousAnswers[linkIds.GynecologicalTemperature]?.valueDecimal,
        previousAnswers[linkIds.GynecologicalTemperatureUnit]?.valueString,
        previousAnswers[linkIds.GynecologicalTemperatureMethod]?.valueCoding?.display,
      ),
      current: formatTemperatureDisplay(
        currentAnswers[linkIds.GynecologicalTemperature]?.valueDecimal,
        currentAnswers[linkIds.GynecologicalTemperatureUnit]?.valueString,
        currentAnswers[linkIds.GynecologicalTemperatureMethod]?.valueCoding?.display,
      ),
    },
    {
      label: "Respiratory Rate",
      previous: isVitalValuePresentForDisplay(
        previousAnswers[linkIds.GynecologicalRespiratoryRate]?.valueInteger,
      )
        ? `${previousAnswers[linkIds.GynecologicalRespiratoryRate]?.valueInteger} bpm`
        : "",
      current: isVitalValuePresentForDisplay(
        currentAnswers[linkIds.GynecologicalRespiratoryRate]?.valueInteger,
      )
        ? `${currentAnswers[linkIds.GynecologicalRespiratoryRate]?.valueInteger} bpm`
        : "",
    },
    {
      label: "Notes",
      previous: previousAnswers[linkIds.GynecologicalNotes]?.valueString ?? "",
      current: currentAnswers[linkIds.GynecologicalNotes]?.valueString ?? "",
    },
  ];

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Vitals</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              {fields.map(({ label, previous, current }) => (
                <Stack gap="0" key={label}>
                  <TextWrapper fw={600}>{label}</TextWrapper>
                  <WordsDiff showRemoved current={current} previous={previous} />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              {fields.map(({ label, previous, current }) => (
                <Stack gap="0" key={label}>
                  <TextWrapper fw={600}>{label}</TextWrapper>
                  <WordsDiff current={current} previous={previous} />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default VitalsSignDiff;
